import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Switch, Text } from '@modusign/design-system';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form-v7';

import SingleDatePicker from '@app/components/DatePicker/SingleDatePicker';
import { BasicInformationFormState } from '@app/features/externalDocument/components/BasicInformationEditor';
import {
  EXTERNAL_DOCUMENT_DEFAULT_FROM_YEAR,
  EXTERNAL_DOCUMENT_DEFAULT_TO_YEAR,
} from '@app/features/externalDocument/constants';

export default function DocumentPeriodInput() {
  const { setValue, watch } = useFormContext<BasicInformationFormState>();
  const dateOfCompletion = watch('dateOfCompletion');

  const periodStartDateController = useController<BasicInformationFormState, 'documentPeriod.from'>(
    {
      name: 'documentPeriod.from',
      defaultValue: null,
    }
  );
  const periodEndDateController = useController<BasicInformationFormState, 'documentPeriod.to'>({
    name: 'documentPeriod.to',
    defaultValue: null,
  });

  const [isEndDateEnabled, setIsEndDateEnabled] = useState(!!periodEndDateController.field.value);

  useEffect(() => {
    if (!periodEndDateController.field.value || !periodStartDateController.field.value) {
      return;
    }

    const from = new Date(periodStartDateController.field.value).getTime();
    const to = new Date(periodEndDateController.field.value).getTime();
    const now = Date.now();

    if (from < now && now < to) {
      setValue('status', 'EFFECTIVE');
    }
  }, [periodStartDateController.field.value, periodEndDateController.field.value, setValue]);

  const getFromDateOfEndDatePeriodPicker = () => {
    const getDate = (date: string | null) => (date ? new Date(date) : undefined);

    const completionDate = getDate(dateOfCompletion);
    const startDate = getDate(periodStartDateController.field.value);

    return completionDate && startDate && moment(completionDate).isSameOrAfter(startDate)
      ? completionDate
      : startDate || completionDate;
  };

  return (
    <Flex
      alignItems={[null, null, 'center']}
      flex="auto"
      flexDirection={['column', null, 'row']}
      flexWrap="wrap"
      sx={{
        // IE에서 gap을 지원안해서 사이 간격 없이 렌더링합니다.
        gap: '12px 18px',
      }}
      width="100%"
    >
      <Flex alignItems="center" flex="auto" flexWrap="nowrap" sx={{ gap: '6px 4px' }}>
        <SingleDatePicker
          captionLayout="dropdown-buttons"
          defaultMonth={
            periodStartDateController.field.value
              ? new Date(periodStartDateController.field.value)
              : undefined
          }
          field={{
            placeholder: 'YYYY.MM.DD',
            onClearDate: () => {
              periodStartDateController.field.onChange(null);
              periodEndDateController.field.onChange(null);
            },
          }}
          fromYear={EXTERNAL_DOCUMENT_DEFAULT_FROM_YEAR}
          toYear={EXTERNAL_DOCUMENT_DEFAULT_TO_YEAR}
          value={
            periodStartDateController.field.value
              ? new Date(periodStartDateController.field.value)
              : undefined
          }
          onSelect={(date) => {
            if (!date) {
              periodStartDateController.field.onChange(null);
              periodEndDateController.field.onChange(null);
              return;
            }
            periodStartDateController.field.onChange(date.toISOString());
            if (
              periodEndDateController.field.value &&
              moment(date).isAfter(periodEndDateController.field.value)
            ) {
              periodEndDateController.field.onChange(null);
            }
          }}
        />
        <Text>~</Text>
        <SingleDatePicker
          captionLayout="dropdown-buttons"
          defaultMonth={
            periodEndDateController.field.value
              ? new Date(periodEndDateController.field.value)
              : undefined
          }
          field={{
            disabled: !isEndDateEnabled,
            onClearDate: () => {
              periodEndDateController.field.onChange(null);
            },
            placeholder: 'YYYY.MM.DD',
          }}
          footer={
            <Box mx="22px" pb={7}>
              <Flex
                alignItems="center"
                as="section"
                bg="notificationBackgroundPrimaryBase"
                border="1px solid"
                borderColor="boxBorderPrimaryBase"
                borderRadius="3px"
                color="textNeutralSlightlyLight"
                px={5}
                py={4}
              >
                <Text color="iconPrimaryDark" fontSize="12px" mr={2}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </Text>
                <Text color="textNeutralSlightlyLight" fontSize="sm">
                  계약 체결/시작일 이전 날짜 선택 불가
                </Text>
              </Flex>
            </Box>
          }
          fromDate={getFromDateOfEndDatePeriodPicker()}
          toYear={2099}
          value={
            periodEndDateController.field.value
              ? new Date(periodEndDateController.field.value)
              : undefined
          }
          onSelect={(date) => {
            if (date) {
              periodEndDateController.field.onChange(date.toISOString());
            }
          }}
        />
      </Flex>
      <Text
        color="textNeutralLight"
        display="flex"
        fontSize="sm"
        sx={{ alignItems: 'center' }}
        whiteSpace="nowrap"
      >
        종료일 입력
        <Box height="20px" ml={2}>
          <Switch
            checked={isEndDateEnabled}
            onClick={() => {
              setIsEndDateEnabled(!isEndDateEnabled);
              if (isEndDateEnabled) {
                periodEndDateController.field.onChange(null);
              }
            }}
          />
        </Box>
      </Text>
    </Flex>
  );
}
